import React, { useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { ReactCropperElement } from "react-cropper";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { mdiFileDocumentOutline, mdiUpload } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import dayjs from "dayjs";

import ChunkedUploader from "common/ChunkUploader";
import { ClipboardClockSVGIcon } from "components/SVGIcons";
import Tooltip from "components/Tooltip";
import DocumentStatusIcon from "components/DocumentStatusIcon";
import { CropModal } from "features/main/HaulPay/Shared/EditFundingRequestV2/Manage/components/CropModal";
import { CROPPED_CANVAS_OPTIONS } from "features/main/HaulPay/Shared/EditFundingRequestV2/Manage/components/imageEditor/cropperUtils";
import { DocumentPreviewModal } from "features/main/HaulPay/Shared/EditFundingRequestV2/Manage/components/DocumentPreviewModal";
import { COLOR_BY_STATUS, UPLOAD_CATEGORY } from "constants/base";
import { routes, UPLOAD_DOCUMENT_URLS } from "constants/routes";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { FileProps } from "models/Base";
import { IOption } from "models/Form";
import { selectUserDetails } from "store/user/selector";

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i;
const isImageFile = (file: File) =>
  (file.type?.startsWith("image/") ?? false) || IMAGE_EXT.test(file.name);

interface AttachmentFile {
  id: string;
  filename: string;
  downloadUrl: string;
  category: string;
  visibleTo: string[];
}

const VISIBILITY_MAP = (visibility: string[]): string | undefined => {
  if (!Array.isArray(visibility) || visibility.length === 0) {
    return undefined;
  }
  const sorted = [...visibility].sort();
  if (JSON.stringify(sorted) === JSON.stringify(["carrier"])) {
    return "Carrier Only";
  }
  if (JSON.stringify(sorted) === JSON.stringify(["debtor", "shipper"])) {
    return "Shipper/Debtor Only";
  }
  if (JSON.stringify(sorted) === JSON.stringify(["carrier", "debtor", "shipper"])) {
    return "Carrier & Shipper/Debtor";
  }
  return undefined;
};

const StatusCell: React.FC<{ item: any }> = ({ item }) => {
  const userDetails = useSelector(selectUserDetails);
  const apolloClient = useApolloClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [category, setCategory] = useState<IOption[]>([]);
  const [visibleTo, setVisibleTo] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragActive, setIsDragActive] = useState(false);
  const cropperRef = useRef<ReactCropperElement>(null);

  const fileCategories = (item.attachments ?? []).flatMap((file: AttachmentFile) => {
    if (!file || !file.category) return [];
    return file.category
      .split(",")
      .map((cat) => cat.trim())
      .filter(Boolean);
  });
  const normalizedStatus = String(item?.status ?? "")
    .toLowerCase()
    .replace(/[\s_]+/g, "");
  const isUploadLocked = ["haulpayreview", "approved", "remoteapproved", "paid"].includes(
    normalizedStatus
  );

  const startUploadFlow = (file: File) => {
    if (isUploadLocked) return;
    setPendingFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setCategory([]);
    setVisibleTo([]);
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPendingFile(null);
    setPreviewUrl("");
    setCategory([]);
    setVisibleTo([]);
  };

  const handleCrop = async () => {
    if (!pendingFile || !cropperRef.current?.cropper) {
      showErrorMessage("No image to crop");
      return false;
    }
    const canvas = cropperRef.current.cropper.getCroppedCanvas(CROPPED_CANVAS_OPTIONS);
    if (!canvas) {
      showErrorMessage("Failed to crop image");
      return false;
    }
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.92)
    );
    if (!blob) {
      showErrorMessage("Failed to crop image");
      return false;
    }

    const baseName = pendingFile.name.replace(/\.[^/.]+$/, "");
    const newFile = new File([blob], `${baseName}.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now()
    });
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(blob));
    setPendingFile(newFile);
    return true;
  };

  const handleUpload = async () => {
    if (!pendingFile || isUploading) return;
    if (!category.length) {
      showErrorMessage("Please select a category before uploading");
      return;
    }
    if (!visibleTo.length) {
      showErrorMessage("Please select who can view this document");
      return;
    }

    if (!userDetails?.factoring_id || !item?.id) {
      setIsUploading(false);
      setUploadProgress(0);
      showErrorMessage("Missing required IDs for upload");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    const fileId = uuidv4();
    const fileWithMeta = pendingFile as FileProps;
    (fileWithMeta as any).categories = category.map((c) => c.value).join(",");
    (fileWithMeta as any).visible_to = visibleTo;

    const uploader = new ChunkedUploader({
      url: "/upload/chunk/",
      urlCallback: `${
        UPLOAD_DOCUMENT_URLS({
          factoring_id: userDetails.factoring_id,
          funding_request_id: item.id
        }).FR_ATTACHMENT
      }/${fileId}`,
      file: fileWithMeta,
      onProgress: (p: number) => setUploadProgress(p)
    });

    try {
      const result: any = await uploader.upload();
      if (!result?.success) throw new Error(result?.error ?? "Upload failed");
      showSuccessMessage(`"${pendingFile.name}" uploaded successfully`);
      closePreview();
      await apolloClient.refetchQueries({ include: "active" });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed";
      showErrorMessage(message);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const openFilePicker = () => {
    if (isUploadLocked || isUploading) return;
    inputRef.current?.click();
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isUploadLocked || isUploading) return;
    const on = e.type === "dragenter" || e.type === "dragover";
    setIsDragActive(on);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (isUploadLocked || isUploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) startUploadFlow(file);
  };

  const FileTooltip: React.FC<{ files: AttachmentFile[] }> = ({ files }) => (
    <div className="flex flex-col gap-3 w-[260px] max-w-[90vw] max-h-[60vh] overflow-y-auto">
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) startUploadFlow(file);
        }}
      />
      <button
        type="button"
        onClick={openFilePicker}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        disabled={isUploadLocked || isUploading}
        className={clsx(
          "flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 py-5 text-sm font-medium transition-colors",
          (() => {
            if (isUploadLocked || isUploading)
              return "cursor-not-allowed border-card text-subtitle opacity-60";
            if (isDragActive) return "border-primary bg-primary/10 text-primary";
            return "border-primary/60 text-primary hover:bg-primary/10";
          })()
        )}>
        <Icon path={mdiUpload} size={1} className="flex-none" />
        <span>
          {isUploadLocked
            ? "Uploads locked for this status"
            : isDragActive
              ? "Drop file to upload"
              : "Click or drop a file to upload"}
        </span>
      </button>

      {files.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="text-xs font-semibold uppercase text-subtitle tracking-wide px-1">
            Uploaded Documents
          </div>
          <ul className="flex flex-col gap-1.5">
            {files.map((file) => {
              const visibility = VISIBILITY_MAP(file.visibleTo);
              return (
                <li
                  key={file.id}
                  className="flex items-start gap-3 rounded-md border border-card bg-background/40 px-3 py-2 transition-colors hover:bg-popover">
                  <Icon
                    path={mdiFileDocumentOutline}
                    size={0.85}
                    className="mt-0.5 flex-none text-subtitle"
                  />
                  <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                    <a
                      href={file.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate text-sm font-medium text-primary hover:underline">
                      {file.filename}
                    </a>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-subtitle">
                      <span className="truncate">{file.category || "Uncategorized"}</span>
                      {visibility && (
                        <>
                          <span className="text-subtitle/60">·</span>
                          <span className="truncate">{visibility}</span>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row gap-2.5">
        <Link
          to={`${routes.HAULPAY_BROKER_FUNDING_REQUEST}/${item?.id}`}
          className={clsx(
            "rounded-3xl justify-center px-3.5 py-1.5 text-xs flex items-center gap-1.5 border",
            COLOR_BY_STATUS[item?.status]
          )}>
          <ClipboardClockSVGIcon className="flex-none size-4" />
          <span className="text-xs">{item?.status}</span>
        </Link>
        <Tooltip
          position="down"
          trigger="click"
          message={<FileTooltip files={item.attachments ?? []} />}>
          <DocumentStatusIcon
            documentTypes={fileCategories}
            contractType={item?.contractType}
            status={item?.status}
          />
        </Tooltip>
      </div>
      <span className="text-sm text-text">
        {dayjs(item?.statusAt).format("MM/DD/YYYY hh:mm A")}
      </span>

      {pendingFile &&
        (isImageFile(pendingFile) ? (
          <CropModal
            isOpen={!!pendingFile}
            onClose={() => {
              if (!isUploading) closePreview();
            }}
            file={{
              id: "pending",
              name: pendingFile.name,
              preview: previewUrl,
              category,
              visibleTo
            }}
            categories={UPLOAD_CATEGORY}
            onCategoryChange={(_id, cat) => setCategory(cat)}
            onVisibilityChange={(_id, v) => setVisibleTo(v)}
            onCrop={handleCrop}
            cropperRef={cropperRef}
            onUpload={handleUpload}
            isUploading={isUploading}
            uploadProgress={uploadProgress}
          />
        ) : (
          <DocumentPreviewModal
            isOpen={!!pendingFile}
            onClose={() => {
              if (!isUploading) closePreview();
            }}
            file={{
              id: "pending",
              name: pendingFile.name,
              preview: previewUrl,
              type: pendingFile.type,
              category,
              visibleTo
            }}
            categories={UPLOAD_CATEGORY}
            onCategoryChange={(_id, cat) => setCategory(cat)}
            onVisibilityChange={(_id, v) => setVisibleTo(v)}
            onUpload={handleUpload}
            isUploading={isUploading}
            uploadProgress={uploadProgress}
          />
        ))}
    </div>
  );
};

export default StatusCell;
